import Vue from "vue";
import { mapGetters } from "vuex";
import {
    formatDateTime,
    stringHelper
} from "@/components/mixins";
import { partSearch } from "@/components/mixins/partSearch.ts";
import PartCard from './PartCard/PartCard.vue';
import { ResourceSearchResults }          from "@/components/ResourceLibrary";

import CatalogSearch from "@/components/Catalog/CatalogSearch.vue"
import { LoadingSpinner } from "@/components";
import PaginationText from "@/components/Elements/PaginationText.vue";
import { PartInfo } from "@/components/Elements/PartInfo";
import { SuggestPart } from "@/components/SuggestPart";
import { FeatureFlagService } from "@/services/FeatureFlagService";
import { constants } from "@/const";
import {
    ErrorLogService,
    RecentActivityService,
    PartsService
} from "@/services/";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default Vue.extend({
    name: "PartSearchPage",
    components: {
        LoadingSpinner,
        PartInfo,
        PaginationText,
        SuggestPart,
        CatalogSearch,
        PartCard,
        ResourceSearchResults,
        DnTable
    },
    mixins: [formatDateTime, stringHelper, partSearch],

    created() {
        this.getRecentPartSearches();
        this.documentsLibraryEnabled = FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.DOCUMENTS_LIBRARY_ENABLED)
        this.performingSearch = true;
        // Get/Set part data from store
        this.partNumberFromStore = this.getSearchTerm;
        this.partSearchData = this.getPartSearchData;

        // Determine if this is from a url to prevent infinite loop on use of Back button
        if (this.searchTerm !== this.partNumberFromStore) {
            this.partSearchFromUrl().then(() => {
                this.showPreviewResults = true;
            });
        } else {
            this.getPartDetails().then(() => {
                this.showPreviewResults = true;
            });
        }
    },
    computed: {
        ...mapGetters([
            "user",
            "getContentItem",
            "tenantDetails",
            "activeAccountId",
            "audienceAndExclusions",
            "audienceIds",
            "getSearchTerm",
            "getPartSearchData"
        ]),
        previewParts() {
            return this.parts.length > this.partsPerPage ? this.parts.slice(0, this.partsPerPage) : this.parts.length > 0 ? this.parts : [];
        },
        previewPartImages() {
            return this.partImages.length > this.partsPerPage ? this.partImages.slice(0, this.partsPerPage) : this.partImages.length > 0 ? this.partImages : [];
        },
        previewXrefParts() {
            return this.xrefParts.length > this.xrefPartsPerPage ? this.xrefParts.slice(0, this.xrefPartsPerPage) : this.xrefParts.length > 0 ? this.xrefParts : [];
        },
        previewXrefPartImages() {
            return this.xrefPartImages.length > this.xrefPartsPerPage ? this.xrefPartImages.slice(0, this.xrefPartsPerPage) : this.xrefPartImages.length > 0 ? this.xrefPartImages : [];
        },

    },
    props: {
        searchTerm: {
            type: String,
            default: ""
        },
        activeView: {
            type: String,
            default: 'SEARCH_RESULTS'
        }
    },
    data: () => ({
        recentSearchesAreLoading: true,
        showPartResults: false,
        showXrefResults: false,
        showPreviewResults: false,
        documentsLibraryEnabled: false,
        recentSearchesData: {},
        recentSearchesTableColumns: [
            { key: "SearchTerm", label: "Term" },
            { key: "SearchDt", label: "Last Searched" }
        ],
        performingSearch: true,
        havePerformedInitialSearch: false,
        partSearchData: null,
        partNumberFromStore: "",
        // parts only data
        parts: {},
        partImages: {},
        totalNumberOfParts: 0,
        totalNumberOfPartPages: 0,
        currentPartPageNumber: 1,
        partsPerPage: 10,
        // xref parts only data
        xrefParts: {},
        xrefPartImages: {},
        totalNumberOfXrefParts: 0,
        totalNumberOfXrefPartPages: 0,
        currentXrefPageNumber: 1,
        xrefPartsPerPage: 10,
        categoryResults: null,
        categoriesAreLoading: true,
    }),
    methods: {
        handleShowCrossResults() {
            if(this.xrefParts.length == 1){
                this.RedirectToPartDetail(this.xrefParts[0].FullPartDetails.OemPartCode)
            }
            this.showXrefResults = true;
            this.showPreviewResults = false;
        },
        handleShowPartResults() {
            if(this.parts.length == 1){
                this.RedirectToPartDetail(this.parts[0].FullPartDetails.OemPartCode)
            }
            this.showPartResults = true;
            this.showXrefResults = false;
            this.showPreviewResults = false;
        },
        handleShowPreviewResults() {
            this.showPartResults = false;
            this.showXrefResults = false;
            this.showPreviewResults = true;
        },
        async getRecentPartSearches() {
            this.recentSearchesAreLoading = true;
            try {
                let result = await RecentActivityService.getRecentPartSearches(this.user.UserId);
                this.recentSearchesData = result;
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                this.recentSearchesAreLoading = false;
            }
        },
        async partSearchFromUrl() {
            try {
                this.showPreviewResults = false;
                this.havePerformedInitialSearch = false;
                this.performingSearch = true;

                let pageNumber = 1;
                this.partSearchData = await this.getPartResults(pageNumber, pageNumber, this.searchTerm);
                this.getPartDetails();
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                this.havePerformedInitialSearch = true;
                this.performingSearch = false;
            }
        },
        async recentPartSearch(searchNumber: string, page: number) {
            try {
                this.havePerformedInitialSearch = false;
                this.performingSearch = true;

                let isQuerySearch = false;
                await this.searchParts(page, page, searchNumber, isQuerySearch);
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                if (this.parts.length > 1) {
                    this.havePerformedInitialSearch = true;
                    this.performingSearch = false;
                }
            }
        },
        async updateParts(partPage: number, xrefPage: number) {
            // used when using pagination buttons
            try {
                this.showPreviewResults = false;
                this.performingSearch = true;
                this.partSearchData = await this.getPartResults(partPage, xrefPage, this.searchTerm);
                this.parts = this.partSearchData.data.PartSearchResults.PartResults
                this.getPartDetails();
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                this.performingSearch = false;
            }
        },
        async getPartDetails() {
            try {
                this.showPreviewResults = false;
                var self = this;
                let audienceIds = this.audienceAndExclusions ? this.audienceAndExclusions.AudienceIds : null;

                //update parts and images each time
                this.parts = this.partSearchData.data.PartSearchResults.PartResults;
                this.partImages = this.partSearchData.data.PartImageUrls;

                if (this.havePerformedInitialSearch == false) {
                    this.totalNumberOfParts = this.partSearchData.data.PartSearchResults.NumberOfResults;
                    this.totalNumberOfPartPages = this.partSearchData.data.PartSearchResults.NumberOfPages;
                }

                this.xrefParts = this.partSearchData.data.CrossReferenceSearchResult.CrossReferenceResults;
                this.xrefPartImages = this.partSearchData.data.XrefPartImageUrls;
                this.totalNumberOfXrefParts = this.partSearchData.data.CrossReferenceSearchResult.NumberOfResults;
                this.totalNumberOfXrefPartPages = this.partSearchData.data.CrossReferenceSearchResult.NumberOfPages;
                if (this.parts) {
                    this.parts.forEach(function (part: any, index: number) {
                        PartsService.FullPartDetails(self.activeAccountId, self.user.UserId, part.PartId, audienceIds).then(fullPartDetail => {
                            //Need to use $set here so that the new FullPartDetails property is added to vue reactivity
                            self.$set(self.parts[index], "FullPartDetails", fullPartDetail.data);
                        }).catch(err => {
                            ErrorLogService.LogException(err);
                        })
                    });
                }

                if (this.xrefParts) {
                    this.xrefParts.forEach(function (xrefPart: any, index: number) {
                        PartsService.FullPartDetails(self.activeAccountId, self.user.UserId, xrefPart.OriginalPartId, audienceIds).then(fullPartDetail => {
                            //Need to use $set here so that the new FullPartDetails property is added to vue reactivity
                            self.$set(self.xrefParts[index], "FullPartDetails", fullPartDetail.data);
                        }).catch(err => {
                            ErrorLogService.LogException(err);
                        });
                    });
                }
                this.getCategories(this.searchTerm).then(result => {
                    this.categoryResults = result;
                })
                    .catch(err => {
                        ErrorLogService.LogException(err);
                    })
                    .finally(() => {
                        this.categoriesAreLoading = false;
                    });
            } catch (err) {
                ErrorLogService.LogAndGetErrorMessage(err);
            } finally {
                this.recentSearchesAreLoading = false;
                this.havePerformedInitialSearch = true;
                this.performingSearch = false;
            }
        },
        ShowSuggestPart() {
            this.activeView = "SUGGEST_PART"
        },
        HideSuggestPart() {
             this.$router.push({ name: "PartSearchPage", query: { searchTerm: this.searchTerm, activeView: 'SEARCH_RESULTS'} });
        },
        ShowStandardPartPagination() {
            return this.performingSearch === false &&
                this.totalNumberOfParts > 0 &&
                this.totalNumberOfPartPages > 1
        },
        navigateToPartSuggestion(){
            this.$router.push({ name: "PartSearchPage", query: { searchTerm: this.searchTerm, activeView: 'SUGGEST_PART'} });
        }
    }
});
